import { BroadcastEvent } from "@enums/BroadcastEvent";
import { ConstantsService } from "@services/constantsService";
import { TranslationService } from "@services/translation.service";
import { StateObject, StateService, Transition } from "@uirouter/angularjs";
import { IWindowService } from "angular";

export interface IBreadcrumb {
    name: string;
    state?: StateObject;
}

export interface IBreadcrumbNameResolver {
    [key: string]: string | number;
    // key example: peripheralNameBreadcrumbKey - uniquePartCommonPart
}

export class BreadcrumbService {

    static $inject = ["$rootScope", "$state", "$window", "$transitions", "translationService", "constantsService"];

    stateCallbacks: (() => void)[] = [];
    breadcrumbNameResolvers: IBreadcrumbNameResolver | {} = {};

    constructor(
        private readonly $rootScope: angular.IScope,
        private readonly $state: StateService,
        private readonly $window: IWindowService,
        private readonly $transitions: Transition,
        private readonly translationService: TranslationService,
        private readonly constantsService: ConstantsService) {

        $transitions.onSuccess({}, () => {
            this.fireCallbacks();
        });


        $rootScope.$on(BroadcastEvent.UpdateBreadcrumbs, () => {
            this.fireCallbacks();
        });
    }

    registerStateCallback(callback: () => void) {
        this.stateCallbacks.push(callback);
    }

    getPageTitle(): string {
        const breadcrumbs = this.getBreadcrumbs();

        return breadcrumbs[breadcrumbs.length - 1]?.name || "Unnamed Page";
    }

    getPageSubtitle() {
        const breadcrumbs = this.getBreadcrumbs();

        const stateData = breadcrumbs[breadcrumbs.length - 1]?.state?.data;

        if (stateData?.subtitleTranslationKey) {
            return this.translationService.t(stateData.subtitleTranslationKey);
        }

        return stateData?.Subtitle || stateData?.subtitle;
    }

    updateDocumentTitle() {
        var pageTitle = "";
        var breadcrumbs = this.getBreadcrumbs();

        if (breadcrumbs) {
            //$window.document.title += translationService.t(toState.data?.translation);
            breadcrumbs.reverse().forEach(b => {
                if (pageTitle) {
                    pageTitle += " - ";
                }

                pageTitle += b.name;
            });

            if (pageTitle) {
                pageTitle += " - ";
            }
        }

        pageTitle += "AddSecure";

        const subDomain = window.location.hostname.split(".")[0]
        if (!this.constantsService.getIsFleet()) {
            pageTitle = `[${subDomain}] ${pageTitle}`;
        }

        this.$window.document.title = pageTitle;
    }

    getBreadcrumbs(): IBreadcrumb[] {
        let breadcrumbs: IBreadcrumb[] = [];

        this.$state.$current.path.forEach((state: StateObject) => {
            let name = "";

            if (state) {
                if (state.data) {
                    // Allow us to ignore certain routes at certain times
                    if (state.abstract && this.constantsService.getIsMobile()) {
                        return;
                    }

                    if (state.data.displayName || state.data.title) {
                        name = state.data.displayName || state.data.title;
                    }
                    else if (state.data.translation) {
                        name = this.translationService.t(state.data.translation);
                    }

                    if (state.data.hasOwnProperty("breadcrumbNameResolverKey") &&
                        this.breadcrumbNameResolvers[state.data.breadcrumbNameResolverKey]) {
                        name = this.breadcrumbNameResolvers[state.data.breadcrumbNameResolverKey];
                    }
                }

                if (!name) {
                    name = state.self?.params?.title;
                }
            }

            if (name && state.navigable) {
                breadcrumbs.push({
                    name: name,
                    state: state
                });
            }
        });

        if (this.$state.current.params?.translationKey) {
            breadcrumbs.push({
                name: this.translationService.t(this.$state.current.params.translationKey),
                state: null
            });
        } else if (this.$state.current.params?.subTitle) {
            breadcrumbs.push({
                name: this.$state.current.params.subTitle,
                state: null
            });
        }

        return breadcrumbs;
    }

    updateNameResolver(nameResolver: IBreadcrumbNameResolver) {
        Object.assign(this.breadcrumbNameResolvers, nameResolver);

        this.updateDocumentTitle();
    }

    // ---

    private fireCallbacks() {

        this.stateCallbacks.forEach(callback => { callback(); })
    }
}
